import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  pickSavedPreprocessing,
  type Aggregation,
  type AggMethod,
  type PreprocessingParams,
  type RamStageCorrection,
  type SavedPreprocessing,
} from "@tbm/shared";
import { api } from "../lib/api";
import { usePreprocessing, useBulkingMa } from "../state/preprocessing";

interface DrawerProps {
  open: boolean;
  onClose: () => void;
}

const AGGS: Aggregation[] = ["raw", "10s", "1m", "5m", "1h", "1d"];
const METHODS: AggMethod[] = ["mean", "max", "min", "first", "last"];

export function PreprocessingDrawer({ open, onClose }: DrawerProps) {
  const pp = usePreprocessing();
  const ma = useBulkingMa();
  const { scheduleSave, saveNow } = useSavedPreprocessingSync();

  // Setter for the saved sections: update the store for an instant plot
  // refresh, then debounce-persist the saved subset to the server.
  const setSaved = <K extends keyof PreprocessingParams>(
    key: K,
    value: PreprocessingParams[K],
  ) => {
    pp.set(key, value);
    scheduleSave();
  };

  const resetAll = () => {
    pp.reset();
    ma.reset();
    saveNow();
  };

  return (
    <>
      <div
        className={clsx(
          "fixed inset-0 bg-black/25 transition-opacity z-40",
          open ? "opacity-100" : "opacity-0 pointer-events-none",
        )}
        onClick={onClose}
      />
      <aside
        className={clsx(
          "fixed top-0 right-0 bottom-0 w-[420px] max-lg:w-full max-lg:max-w-none z-50 bg-surface1 border-l border-border1",
          "transform transition-transform duration-200",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b border-border1">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim">Tools</div>
            <h2 className="text-[15px] font-semibold text-text">Preprocessing</h2>
          </div>
          <button onClick={onClose} className="text-muted hover:text-text text-lg">×</button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto h-[calc(100%-64px)]">
          <GroupLabel>This session only</GroupLabel>
          <Section title="Aggregation">
            <Segmented
              options={AGGS}
              value={pp.aggregation}
              onChange={(v) => pp.set("aggregation", v)}
              labels={{ raw: "Raw", "10s": "10s", "1m": "1m", "5m": "5m", "1h": "1h", "1d": "1d" }}
            />
            <label className="block mt-3 text-[11px] tracking-wider uppercase text-dim">Method</label>
            <Segmented
              options={METHODS}
              value={pp.aggMethod}
              onChange={(v) => pp.set("aggMethod", v)}
              labels={{ mean: "Mean", max: "Max", min: "Min", first: "First", last: "Last" }}
            />
          </Section>

          <Section
            title="Bulking factor smoothing"
            subtitle="Weighted moving-average line over the Bulking Factor trace"
          >
            <Toggle
              label="Show smoothed line"
              value={ma.enabled}
              onChange={(v) => ma.set("enabled", v)}
            />
            <NumberField
              label="Distance forward"
              suffix="m"
              value={ma.forwardM}
              onChange={(v) => ma.set("forwardM", Math.max(0.1, v))}
              step={0.5}
            />
            <NumberField
              label="Distance back"
              suffix="m"
              value={ma.backM}
              onChange={(v) => ma.set("backM", Math.max(0.1, v))}
              step={0.5}
            />
          </Section>

          <GroupLabel>Saved — applies for all viewers</GroupLabel>
          <Section title="Chainage filter" subtitle="Monotonic increment tolerance for Chainage">
            <NumberField
              label="Allowable increase"
              suffix="m"
              value={pp.chainageAllowableIncreaseM}
              onChange={(v) => setSaved("chainageAllowableIncreaseM", v)}
              step={0.5}
            />
            <NumberField
              label="Allowable decrease"
              suffix="m"
              value={pp.chainageAllowableDecreaseM}
              onChange={(v) => setSaved("chainageAllowableDecreaseM", v)}
              step={0.1}
            />
          </Section>

          <Section title="Jacking force" subtitle="Inactivity mask + rolling-window outlier filter">
            <Toggle
              label="Enable rolling filter"
              description="Drop samples more than N σ from the rolling mean"
              value={pp.jackingRollingEnabled}
              onChange={(v) => setSaved("jackingRollingEnabled", v)}
            />
            <NumberField
              label="Min jacking force"
              suffix="kN"
              value={pp.jackingMinKN}
              onChange={(v) => setSaved("jackingMinKN", Math.max(0, v))}
              step={5}
            />
            <NumberField
              label="Rolling window"
              suffix="samples"
              value={pp.jackingRollingWindow}
              onChange={(v) => setSaved("jackingRollingWindow", Math.max(2, Math.round(v)))}
              step={5}
            />
            <NumberField
              label="Sigma threshold"
              suffix="σ"
              value={pp.jackingRollingSigma}
              onChange={(v) => setSaved("jackingRollingSigma", v)}
              step={0.5}
            />
          </Section>

          <RamStageSection />

          <Section title="Secondary analysis">
            <NumberField
              label="Bulking factor multiplier"
              value={pp.bulkingFactorBaseline}
              onChange={(v) => setSaved("bulkingFactorBaseline", v)}
              step={0.05}
            />
            <Toggle
              label="Zero-filter samples"
              description="Drop rows where Torque, RPM, or Advance Rate are zero"
              value={pp.zeroFilter}
              onChange={(v) => setSaved("zeroFilter", v)}
            />
            <Toggle
              label="Bulking factor: mining only"
              description="Restrict to bypass_status == 2"
              value={pp.miningOnly}
              onChange={(v) => setSaved("miningOnly", v)}
            />
          </Section>

          <button
            onClick={resetAll}
            className="w-full border border-border2 hover:border-wb text-muted hover:text-wb transition-colors rounded px-3 py-2 text-[12px] uppercase tracking-widest"
          >
            Reset to defaults
          </button>
        </div>
      </aside>
    </>
  );
}

function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="text-[14px] font-semibold tracking-[0.14em] uppercase text-text">{title}</h3>
      {subtitle && <p className="text-[11.5px] text-muted mt-1 mb-3">{subtitle}</p>}
      {!subtitle && <div className="mb-3" />}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function NumberField({
  label,
  suffix,
  value,
  onChange,
  step = 1,
  placeholder,
}: {
  label: string;
  suffix?: string;
  value: number;
  onChange: (v: number) => void;
  step?: number;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <div className="text-[11px] text-muted mb-1.5">{label}</div>
      <div className="flex items-center bg-surface2 border border-border1 rounded px-3 py-2 focus-within:border-wb/60">
        <input
          type="number"
          value={Number.isFinite(value) ? value : ""}
          placeholder={placeholder}
          step={step}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 bg-transparent outline-none mono-nums text-text text-[14px] placeholder:text-dim"
        />
        {suffix && <span className="text-dim text-[11px] ml-2">{suffix}</span>}
      </div>
    </label>
  );
}

function Toggle({
  label,
  description,
  value,
  onChange,
}: {
  label: string;
  description?: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-4 cursor-pointer">
      <div>
        <div className="text-[13px] text-text">{label}</div>
        {description && <div className="text-[11px] text-dim mt-0.5">{description}</div>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={clsx(
          "relative h-5 w-9 rounded-full transition-colors flex-shrink-0",
          value ? "bg-wb" : "bg-border2",
        )}
      >
        <span
          className={clsx(
            "absolute top-0.5 h-4 w-4 bg-white rounded-full transition-transform",
            value ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </button>
    </label>
  );
}

/** Small heading dividing the drawer into session vs. saved groups. */
function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[10.5px] uppercase tracking-[0.22em] text-dim border-b border-border1 pb-2">
      {children}
    </div>
  );
}

/**
 * Debounced persistence for the saved preprocessing sections. The
 * preprocessing store stays the single client-side source of truth;
 * this just mirrors its saved subset to /api/drive/preprocessing so the
 * settings survive a refresh and are shared with every other viewer.
 */
function useSavedPreprocessingSync() {
  const qc = useQueryClient();
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mutation = useMutation({
    mutationFn: (saved: SavedPreprocessing) => api.setSavedPreprocessing(saved),
    onSuccess: (info) => qc.setQueryData(["drive"], info),
  });
  const flush = () => {
    mutation.mutate(pickSavedPreprocessing(usePreprocessing.getState()));
  };
  const scheduleSave = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, 400);
  };
  const saveNow = () => {
    if (timer.current) clearTimeout(timer.current);
    flush();
  };
  return { scheduleSave, saveNow };
}

/**
 * Ram-stage correction. Like the other saved sections it's a per-drive
 * setting persisted server-side, but it carries its own state because
 * the JF prediction model consumes the corrected Jacking Force. Edits
 * save to /api/drive/ram-stage-correction (debounced) and bust the JF
 * cache.
 */
function RamStageSection() {
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  return (
    <Section
      title="Ram stage correction"
      subtitle="Correct Jacking Force for multistage rams. Default values are for Heynick 3 stage rams with the container calibrated to stage 2. Calibrate stage 1 length with clusters in plot. Also fed to the Jacking Force prediction as the corrected measured force."
    >
      {drive.data ? (
        <RamStageFields initial={drive.data.ramStageCorrection} />
      ) : (
        <div className="text-[11px] text-dim">Loading…</div>
      )}
    </Section>
  );
}

function RamStageFields({ initial }: { initial: RamStageCorrection }) {
  const qc = useQueryClient();
  const [local, setLocal] = useState<RamStageCorrection>(initial);
  const mutation = useMutation({
    mutationFn: (next: RamStageCorrection) => api.setRamStageCorrection(next),
    onSuccess: (info) => qc.setQueryData(["drive"], info),
  });
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Update the inputs immediately; debounce the server write so a burst
  // of keystrokes doesn't fire a PUT (and a JF recompute) each.
  const apply = (next: RamStageCorrection) => {
    setLocal(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => mutation.mutate(next), 400);
  };

  return (
    <>
      <Toggle
        label="Enable ram stage correction"
        value={local.enabled}
        onChange={(v) => apply({ ...local, enabled: v })}
      />
      <NumberField
        label="Stage 1 ram length used"
        suffix="m"
        value={local.stage1LengthM}
        onChange={(v) => apply({ ...local, stage1LengthM: Math.max(0, v) })}
        step={0.1}
      />
      <NumberField
        label="Stage 1 correction ratio"
        value={local.stage1Ratio}
        onChange={(v) => apply({ ...local, stage1Ratio: Math.max(0, v) })}
        step={0.01}
      />
      <NumberField
        label="Later stage correction ratio"
        value={local.laterStageRatio}
        onChange={(v) => apply({ ...local, laterStageRatio: Math.max(0, v) })}
        step={0.01}
      />
    </>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
  labels,
}: {
  options: readonly T[];
  value: T;
  onChange: (v: T) => void;
  labels: Record<T, string>;
}) {
  return (
    <div className="grid grid-flow-col auto-cols-fr bg-surface2 border border-border1 rounded overflow-hidden mt-1">
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o)}
          className={clsx(
            "py-1.5 text-[12px] tracking-wider uppercase transition-colors",
            value === o ? "bg-wb text-white" : "text-muted hover:bg-surface3 hover:text-text",
          )}
        >
          {labels[o]}
        </button>
      ))}
    </div>
  );
}
